// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc */

import { addMevtCookie } from '@ta/common.tracking';
import type { FetcherResult } from './Typeahead';

type EventType = 'start' | 'select' | 'search';

/** The type of user interaction with the typeahead, used for tracking */
export type UserInteractionType = 'click' | 'enter' | 'start';

type TrackingDataProps = {|
  eventType: EventType,
  userInteractionType: UserInteractionType,
  selectedItem?: FetcherResult,
|};

/**
 * Tracking object to hold and track typeahead state. Since we are logging various pieces of data from across the
 * component hierarchy it is easier to encapsulate state in this object. Note that there is an expectation that when
 * the user starts interacting with the typeahead that 'setInteractionStart()' is called with the corresponding
 * timestamp.
 */
class TypeaheadTracking {
  /** The time in ms of when the user started interacting with the typeahead */
  interactionStart: number;

  /**
   * An optional label to indicate the UI origin of this typeahead. This gets sent to
   * /TypeAheadJson?action=RECORD
   */
  uiOrigin: ?string;

  /** The search session ID */
  searchSessionId: string;

  constructor(uiOrigin: ?string, searchSessionId: string) {
    this.uiOrigin = uiOrigin;
    this.searchSessionId = searchSessionId;
  }

  setInteractionStart = (interactionStart: number) => {
    this.interactionStart = interactionStart;
  };

  createTrackingData = ({ eventType, userInteractionType, selectedItem }: TrackingDataProps) => {
    const query = selectedItem && selectedItem.query;
    const selectedValue = selectedItem && selectedItem.value;
    const selectedUrl = selectedItem && selectedItem.url;
    const resultIndex = selectedItem && selectedItem.resultIndex;
    const selectedPlaceType = selectedItem && selectedItem.type;
    const selectedDataType = selectedItem && selectedItem.data_type;
    const totalCount = selectedItem && selectedItem.totalCount;
    const documentId = selectedItem && selectedItem.document_id;

    const data = {
      action: 'RECORD',
      eventType,
      uiOrigin: this.uiOrigin,
      type: selectedPlaceType,
      value: selectedValue,
      source: this.uiOrigin,
      startTime: String(this.interactionStart),
      selDepth: isNaN(resultIndex) ? undefined : resultIndex + 1, // selDepth starts at 1
      isChild: 'false',
      totalNum: totalCount ? String(totalCount) : undefined,
      userInteractionType,
      scope: '1',
      query,
      dataType: selectedDataType,
      url: selectedUrl,
      searchSessionId: this.searchSessionId,
      documentId,
    };

    const params: { [string]: string } = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (typeof value !== 'undefined' && value !== null) {
        params[key] = String(data[key]);
      }
    });

    return params;
  };

  ajaxTrack = (props: TrackingDataProps) => {
    const data = this.createTrackingData(props);
    fetch(`/TypeAheadJson?${new URLSearchParams(data).toString()}`, {
      credentials: 'same-origin',
    });
  };

  /**
   * Track that interaction has started with the typeahead
   */
  trackInteractionStart = () => {
    this.ajaxTrack({ eventType: 'start', userInteractionType: 'start' });
  };

  /**
   * Track that the user has made a selection
   */
  trackSelection = (selectedItem: FetcherResult, userInteractionType: UserInteractionType) => {
    this.ajaxTrack({ eventType: 'select', userInteractionType, selectedItem });
  };

  trackEventViaMEVTCookie = (
    selectedItem: FetcherResult,
    userInteractionType: UserInteractionType,
  ) => {
    const data = this.createTrackingData({
      eventType: 'search',
      userInteractionType,
      selectedItem,
    });
    addMevtCookie({
      ...data,
      t: 'ta',
    });
  };
}

export default TypeaheadTracking;
